import React from 'react';
import Layout from '@/components/Layout';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import contentfulService from '@/services/contentfulService';
import { useIsMobile } from '@/hooks/use-mobile';
import BrowseLoadingState from '@/components/browse/BrowseLoadingState';
import BrowseErrorState from '@/components/browse/BrowseErrorState';
import BrowseMobileLayout from '@/components/browse/BrowseMobileLayout';
import BrowseDesktopLayout from '@/components/browse/BrowseDesktopLayout';
import useBrowseContent from '@/hooks/useBrowseContent';
const Browse: React.FC = () => {
  const {
    resolvedTheme
  } = useTheme();
  const isMobile = useIsMobile();
  const bgColor = resolvedTheme === 'dark' ? 'bg-netflix-black' : 'bg-background';
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';

  // Fetch stories using React Query
  const {
    data: stories = [],
    isLoading: storiesLoading,
    error: storiesError
  } = useQuery({
    queryKey: ['stories'],
    queryFn: contentfulService.getStories
  });

  // Get unique categories from stories
  const categories = ['all', ...Array.from(new Set(stories.map(story => story.category.toLowerCase())))];

  // Fetch tags from Contentful blogPost content type
  const {
    data: tags = [],
    isLoading: tagsLoading
  } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      try {
        // Attempt to get tags from contentfulService if available
        if (contentfulService.getAllTags) {
          return await contentfulService.getAllTags();
        }

        // Fallback to extracting tags from stories
        const allTags = stories.flatMap(story => story.tags || []);
        return Array.from(new Set(allTags)).sort();
      } catch (error) {
        console.error("Error fetching tags:", error);
        return [];
      }
    },
    enabled: !storiesLoading && stories.length > 0
  });
  const isLoading = storiesLoading || tagsLoading;
  const error = storiesError;

  // Use the custom hook to handle browse state and filters
  const {
    filteredStories,
    activeCategory,
    activeTag,
    categoryCounts,
    activeCategoryDescription,
    handleCategoryChange,
    handleTagChange
  } = useBrowseContent(stories, categories);
  if (isLoading) {
    return <Layout><BrowseLoadingState /></Layout>;
  }
  if (error) {
    return <Layout><BrowseErrorState /></Layout>;
  }
  return (
    <Layout>
      <div className={cn("min-h-screen", bgColor)}>
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 lg:px-12 xl:px-16 py-8">
          {isMobile ? (
            <BrowseMobileLayout 
              filteredStories={filteredStories} 
              categories={categories} 
              activeCategory={activeCategory} 
              onCategoryChange={handleCategoryChange} 
              categoryCounts={categoryCounts} 
              tags={tags} 
              activeTag={activeTag} 
              onTagChange={handleTagChange} 
              activeCategoryDescription={activeCategoryDescription} 
            />
          ) : (
            <BrowseDesktopLayout 
              filteredStories={filteredStories} 
              categories={categories} 
              activeCategory={activeCategory} 
              onCategoryChange={handleCategoryChange} 
              categoryCounts={categoryCounts} 
              tags={tags} 
              activeTag={activeTag} 
              onTagChange={handleTagChange} 
              activeCategoryDescription={activeCategoryDescription} 
            />
          )}
        </div>
      </div>
    </Layout>
  );
};
export default Browse;